import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { cx } from '@/utils';
import { Tab, Tabs, TabsBody, TabsHeader } from '@material-tailwind/react';
import { FacebookEmbed, InstagramEmbed, TikTokEmbed, YouTubeEmbed } from 'react-social-media-embed';

import SvgDiscord from '@/components/common/Icons/Discord';
import SvgFacebook from '@/components/common/Icons/Facebook';
import SvgInstagram from '@/components/common/Icons/Instagram';
import SvgLinkedIn from '@/components/common/Icons/LinkedIn';
import SvgPinterest from '@/components/common/Icons/Pinterest';
import SvgReddit from '@/components/common/Icons/Reddit';
import SvgSnapchat from '@/components/common/Icons/Snapchat';
import SvgSpotify from '@/components/common/Icons/Spotify';
import SvgTelegram from '@/components/common/Icons/Telegram';
import SvgTikTok from '@/components/common/Icons/TikTok';
import SvgTwitch from '@/components/common/Icons/Twitch';
import SvgTwitter from '@/components/common/Icons/Twitter';
import SvgWhatsApp from '@/components/common/Icons/WhatsApp';
import SvgYoutube from '@/components/common/Icons/Youtube';
import {
  SocialNetworkType,
  type Maybe,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { buttonVariants } from '@/components/ui/Button';

import { type BlockStyle } from '../../../PageSnsBlock/public/News';

type NewsSocialMediaProps = {
  items: { id: string; snsSocialNetworkType?: Maybe<SocialNetworkType>; link?: Maybe<string> }[];
  blockStyle: BlockStyle;
};

// Mapeo de iconos para todas las redes sociales
const SOCIAL_NETWORK_ICONS: Record<SocialNetworkType, React.ReactNode> = {
  [SocialNetworkType.Discord]: <SvgDiscord />,
  [SocialNetworkType.Facebook]: <SvgFacebook />,
  [SocialNetworkType.Instagram]: <SvgInstagram />,
  [SocialNetworkType.LinkedIn]: <SvgLinkedIn />,
  [SocialNetworkType.Pinterest]: <SvgPinterest />,
  [SocialNetworkType.Reddit]: <SvgReddit />,
  [SocialNetworkType.Snapchat]: <SvgSnapchat />,
  [SocialNetworkType.Spotify]: <SvgSpotify />,
  [SocialNetworkType.Telegram]: <SvgTelegram />,
  [SocialNetworkType.TikTok]: <SvgTikTok />,
  [SocialNetworkType.Twitch]: <SvgTwitch />,
  [SocialNetworkType.Twitter]: <SvgTwitter />,
  [SocialNetworkType.WhatsApp]: <SvgWhatsApp />,
  [SocialNetworkType.YouTube]: <SvgYoutube />,
};

// Mapeo de nombres para display (para mostrar "X" en lugar de "Twitter")
const SOCIAL_NETWORK_DISPLAY_NAMES: Record<SocialNetworkType, string> = {
  [SocialNetworkType.Discord]: 'Discord',
  [SocialNetworkType.Facebook]: 'Facebook',
  [SocialNetworkType.Instagram]: 'Instagram',
  [SocialNetworkType.LinkedIn]: 'LinkedIn',
  [SocialNetworkType.Pinterest]: 'Pinterest',
  [SocialNetworkType.Reddit]: 'Reddit',
  [SocialNetworkType.Snapchat]: 'Snapchat',
  [SocialNetworkType.Spotify]: 'Spotify',
  [SocialNetworkType.Telegram]: 'Telegram',
  [SocialNetworkType.TikTok]: 'TikTok',
  [SocialNetworkType.Twitch]: 'Twitch',
  [SocialNetworkType.Twitter]: 'X',
  [SocialNetworkType.WhatsApp]: 'WhatsApp',
  [SocialNetworkType.YouTube]: 'YouTube',
};

// Helper para obtener el nombre de display
const getDisplayName = (type: SocialNetworkType): string =>
  SOCIAL_NETWORK_DISPLAY_NAMES[type] || type;

export default function NewsSocialMedia({ items, blockStyle }: NewsSocialMediaProps) {
  const dataOrder = items.sort((a: any, b: any) => a.orderIndex - b.orderIndex);

  const [activeTab, setActiveTab] = useState<TActiveTab>({
    type: dataOrder[0]?.snsSocialNetworkType,
    link: dataOrder[0]?.link,
  });

  if (!dataOrder.length) return null;
  return (
    <>
      <Tabs value={activeTab.type}>
        <TabsHeader
          className={'gap-[13px] bg-white p-0'}
          indicatorProps={{ className: 'bg-transparent shadow-none rounded-none' }}
        >
          {dataOrder.map(
            (item) =>
              item.snsSocialNetworkType && (
                <Tab
                  key={item.id}
                  value={item.snsSocialNetworkType}
                  onClick={() =>
                    setActiveTab({
                      type: item.snsSocialNetworkType,
                      link: item.link,
                    })
                  }
                  className={cx(
                    '!z-base w-fit border-hawkesBlue text-center font-medium',
                    blockStyle.isB && 'min-w-[125px] rounded-t-[10px] border-x-2 border-t-2 py-2',
                    blockStyle.isA && 'min-w-[100px] rounded-t-[4px] border-2 py-1',
                    activeTab.type === item.snsSocialNetworkType &&
                      blockStyle.isB &&
                      'border-primary bg-primary text-white',
                    activeTab.type === item.snsSocialNetworkType &&
                      blockStyle.isA &&
                      'border-b-primary bg-primary/10 text-primary'
                  )}
                >
                  {getDisplayName(item.snsSocialNetworkType)}
                </Tab>
              )
          )}
        </TabsHeader>
        <TabsBody>
          {blockStyle.isB && (
            <div className="flex items-center justify-between rounded-se-[10px] bg-primary p-[10px]">
              <p className="font-montserrat font-semibold text-white">Más información</p>
              <Link
                href={activeTab.link ?? '#'}
                target="_blank"
                className={cx(
                  buttonVariants({ variant: 'ghost', size: 'small' }),
                  'hover:bg-initial min-w-[125px] bg-white'
                )}
              >
                {activeTab.type && SOCIAL_NETWORK_ICONS[activeTab.type]}
                Follow
              </Link>
            </div>
          )}
          <div className={'relative min-h-[500px] w-full overflow-auto'}>
            <PostSocialNetwork activeTab={activeTab} />
          </div>
        </TabsBody>
      </Tabs>
    </>
  );
}

type TActiveTab = {
  type?: SocialNetworkType | null;
  link?: string | null;
};

type TPostSocialNetwork = {
  activeTab: TActiveTab;
};

// Componente para renderizar el embed de cada red social
const PostSocialNetwork = ({ activeTab }: TPostSocialNetwork) => {
  if (!activeTab.type || !activeTab.link) return null;

  // Facebook - usar Page Plugin para páginas o FacebookEmbed para posts
  if (activeTab.type === SocialNetworkType.Facebook) {
    // Detectar si es un post, foto, video o contenido específico (no una página)
    const isPost =
      activeTab.link.includes('/posts/') ||
      activeTab.link.includes('/photos/') ||
      activeTab.link.includes('/photo/') ||
      activeTab.link.includes('/photo?') ||
      activeTab.link.includes('/videos/') ||
      activeTab.link.includes('/watch') ||
      activeTab.link.includes('/reel/') ||
      activeTab.link.includes('fbid=');

    if (isPost) {
      return <FacebookEmbed url={activeTab.link} width="100%" />;
    }

    return <FacebookPageEmbedded link={activeTab.link} />;
  }

  // Twitter/X - El embed fue removido debido a rate limits constantes de la API de Twitter
  // Ahora usa el fallback genérico con botón de enlace

  // YouTube embed
  if (activeTab.type === SocialNetworkType.YouTube) {
    return <YouTubeEmbed url={activeTab.link} width="100%" />;
  }

  // TikTok embed
  if (activeTab.type === SocialNetworkType.TikTok) {
    return (
      <div className="flex w-full justify-center py-4">
        <div style={{ maxWidth: '325px', width: '100%' }}>
          <TikTokEmbed url={activeTab.link} width="100%" />
        </div>
      </div>
    );
  }

  // Instagram embed
  if (activeTab.type === SocialNetworkType.Instagram) {
    return <InstagramEmbed url={activeTab.link} width="100%" captioned={false} />;
  }

  // Para otras redes sociales, mostrar un botón de enlace
  return (
    <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-4 p-8">
      <div className="text-6xl text-primary">{SOCIAL_NETWORK_ICONS[activeTab.type]}</div>
      <p className="text-center text-lg text-gray-600">
        Visita nuestro perfil en {getDisplayName(activeTab.type)}
      </p>
      <Link
        href={activeTab.link}
        target="_blank"
        rel="noopener noreferrer"
        className={cx(buttonVariants({ variant: 'default', size: 'default' }), 'gap-2')}
      >
        {SOCIAL_NETWORK_ICONS[activeTab.type]}
        Ir a {getDisplayName(activeTab.type)}
      </Link>
    </div>
  );
};

// Para páginas de Facebook, usar el Page Plugin mediante iframe
// Facebook requires a numeric `width` query param. Clamp it to [180,500]
// and center the iframe so it appears symmetric on different viewports.
const FacebookPageEmbedded = ({ link }: { link: string }) => {
  const encodedUrl = encodeURIComponent(link);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const MIN_W = 180;
    const MAX_W = 500;

    const updateWidth = () => {
      const el = containerRef.current;
      if (!el) return;
      const measured = Math.floor(el.clientWidth);
      const clamped = Math.min(MAX_W, Math.max(MIN_W, measured));
      setContainerWidth(clamped);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateWidth());
      ro.observe(containerRef.current!);
      return () => ro.disconnect();
    }

    const onResize = () => updateWidth();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [link]);

  const widthParam = containerWidth ?? 500;

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div
        ref={containerRef}
        className="w-full"
        style={{ width: '100%', maxWidth: `${widthParam}px`, margin: '0 auto' }}
      >
        <iframe
          src={`https://www.facebook.com/plugins/page.php?href=${encodedUrl}&tabs=timeline&width=${widthParam}&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true`}
          width="100%"
          height="600"
          style={{ border: 'none', overflow: 'hidden', display: 'block' }}
          scrolling="no"
          frameBorder="0"
          allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        />
      </div>
    </div>
  );
};

function getProfileFromUrl(tweetUrl: string): string {
  const tweetPattern = /https:\/\/twitter\.com\/([^/]+)\/status\/(\d+)/;
  const profilePattern = /https:\/\/twitter\.com\/([^/]+)\/?/;
  const xPattern = /https:\/\/x\.com\/([^/]+)\/?/;

  const tweetMatch = tweetUrl.match(tweetPattern);
  const profileMatch = tweetUrl.match(profilePattern);
  const xMatch = tweetUrl.match(xPattern);

  if (tweetMatch) return tweetMatch[1];
  if (profileMatch) return profileMatch[1];
  if (xMatch) return xMatch[1];

  console.error('URL de Twitter no válida.');
  return '';
}
